import { cloneDeep } from 'lodash'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'

import { _getSSelectItemDataById, _getSDataById } from '../suppliers/mockData'
import {
  getOrderDataById,
  getOrderOverviewData,
  getOrderListData,
  _getMOSelectItemDataById,
} from '../orders/mockData'
import { getOrderMSServiceListData } from '../orderMSServices/mockData'
import {
  getCompanyListData,
  getCompanyDataById,
  getCompanyOverviewData,
} from '../companies/mockData'
import { getCompanyMSServiceListData , propagateCompanyMSServiceChanges } from '../companyMSServices/mockData'
import { getCompanyMasterServiceListData } from '../companyMasterServices/mockData'
import { _getSCTDataById } from './../supplierContacts/mockData'
import { _getTSelectItemDataById } from '../tsds/mockData'
import { _getOSelectItemDataById } from '../opportunities/mockData'
import {
  getCompanyContactListData,
  _getCCTDataById,
} from '../companyContacts/mockData'
import { getTsdContactListData, _getTCTDataById } from '../tsdContacts/mockData'
import {
  getContractStatusById,
  ContractStatusIds,
} from '~/services/selectLists/contractStatii'
import {
  getSupplierTypeById,
  SupplierTypeIds,
} from '~/services/selectLists/supplierTypes'
import { getPersonListData, _getPDataById } from '../persons/mockData'
import { getSupplierContactRoleListData , _getSCRDataById } from '../selectLists/supplierContactRoles/mockData'
import { getAllMSServicesFromSiloOverview } from '~/cynqueModules/siloRelated/getAllMSServicesFromSiloOverview'
import { propagateContractData } from '~/cynqueModules/dataObjects/Contract'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'

import { getContractDocumentListDataByContractId } from '../contractDocuments/mockData'
import { getContractNoteListDataByContractId } from '../contractNotes/mockData'
import { getContactStatusById } from './../../../../services/selectLists/contactStatii'
import { _trimContractChildArraysToSilo } from './trimContractChildArraysToSilo'
import { ContactStatusIds } from '~/services/selectLists/contactStatii'
import {
  getContractTypeById,
  ContractTypeIds,
} from '~/services/selectLists/contractTypes'
import { assertIsNotUndefined } from '~/types/models'
import type { ICompanyMSService } from '~/types/company/models'
import type { IOpportunity } from '~/types/opportunity/models'
import type { IContract } from '~/types/contract/models'
import type { TEntityId } from '~/types/models'

// Re-export so other overview-assembly mock modules (e.g. companies) can trim
// their contracts' child arrays through the same contracts entrypoint.
export { _trimContractChildArraysToSilo }

const mn = '[api/msw/api/contracts/mockData.ts]'

const contractListData = undefined as unknown as IContract[]

export const getContractListData = getListDataFunctionFactory<IContract[]>({
  listData: contractListData,
  storageKey: 'contracts',
  getStartingListData: getStartingContractListData,
  forOfFunction: async (contract) => {
    contract.contractDocuments = await getContractDocumentListDataByContractId(
      contract.id
    )
    contract.contractNotes = await getContractNoteListDataByContractId(
      contract.id
    )
  },
  sortFunction: (a, b) => {
    if (b.createdAt > a.createdAt) {
      return -1
    }
    if (b.createdAt < a.createdAt) {
      return 1
    }
    return 0
  },
})

export async function getContractDataById(
  id: TEntityId
): Promise<IContract | undefined> {
  const fn = `${mn} getContractDataById |`
  const list = await getContractListData()
  if (!list) {
    throw new Error(`${fn} contractList returned undefined/null`)
  }
  return list.find((contract: IContract) => contract.id === id)
}

export async function getContractListDataByCompanyId(
  id: TEntityId
): Promise<IContract[]> {
  const fn = `${mn} getContractListDataByCompanyId |`
  const list = await getContractListData()
  if (!list) {
    throw new Error(`${fn} contractList returned undefined/null`)
  }
  return (
    list.filter(
      (contract: IContract) =>
        contract.company?.id === id || contract.contact?.company?.id === id
    ) ?? []
  )
}

export async function getContractListDataByOpportunityId(
  id: TEntityId
): Promise<IContract[]> {
  const fn = `${mn} getContractListDataByOpportunityId |`
  const list = await getContractListData()
  if (!list) {
    throw new Error(`${fn} contractList returned undefined/null`)
  }
  return (
    list.filter(
      (contract: IContract) =>
        contract.opportunities &&
        contract.opportunities.find(
          (opportunity: IOpportunity) => opportunity.id === id
        )
    ) ?? []
  )
}

export async function getContractListDataByOrderId(
  id: TEntityId
): Promise<IContract[]> {
  const fn = `${mn} getContractListDataByOrderId |`
  const list = await getContractListData()
  if (!list) {
    throw new Error(`${fn} contractList returned undefined/null`)
  }
  return (
    list.filter(
      (contract: IContract) => contract.order && contract.order.id === id
    ) ?? []
  )
}

export async function getOpportunityOverviewContractListData({
  companyId,
  opportunityId,
}: {
  companyId: TEntityId
  opportunityId: TEntityId
}) {
  const fn = `${mn} getOpportunityOverviewContractListData |`
  console.debug(`${fn} running`)
  return await _getOpportunityOrOrderOverviewContractListData({
    companyId,
    opportunityOrOrderId: opportunityId,
    entityName: 'opportunity',
  })
}

export async function getOrderOverviewContractListData({
  companyId,
  orderId,
}: {
  companyId: TEntityId
  orderId: TEntityId
}) {
  const fn = `${mn} getOrderOverviewContractListData |`
  console.debug(`${fn} running`)
  return await _getOpportunityOrOrderOverviewContractListData({
    companyId,
    opportunityOrOrderId: orderId,
    entityName: 'order',
  })
}

async function _getOpportunityOrOrderOverviewContractListData({
  companyId,
  opportunityOrOrderId,
  entityName,
}: {
  companyId: TEntityId
  opportunityOrOrderId: TEntityId
  entityName: string
}) {
  const fn = `${mn} get${entityName}OverviewContractListData |`
  console.debug(`${fn} running`)
  const list = await getContractListDataByCompanyId(companyId)
  if (!list) {
    console.debug(`${fn} no contracts found for company id ${companyId}`)
    return []
  }
  const contracts =
    list.filter(
      (contract: IContract) =>
        contract.contractType.id === ContractTypeIds.MSA ||
        contract[entityName]?.id === opportunityOrOrderId
    ) ?? []
  return _trimContractChildArraysToSilo(
    contracts,
    entityName as 'opportunity' | 'order',
    opportunityOrOrderId
  )
}

//
// Propagation methods
//

export async function propagateContractAdditions({
  newEntityData,
}: {
  newEntityData: IContract
}) {
  const fn = `${mn} propagateContractAdditions |`
  if (!newEntityData) {
    console.debug(`${fn} newEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateContractAdditionsChangesOrDeletions({
    sourceContract: newEntityData,
  })
}

export async function propagateContractChanges({
  targetItem,
}: {
  targetItem: IContract
}) {
  const fn = `${mn} propagateContractChanges |`
  if (!targetItem) {
    console.debug(`${fn} updatedEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateContractAdditionsChangesOrDeletions({
    sourceContract: targetItem,
  })
}

export async function propagateContractDeletions({
  deletedEntityData,
}: {
  deletedEntityData: IContract
}) {
  const fn = `${mn} propagateContractDeletions |`
  if (!deletedEntityData) {
    console.debug(`${fn} deletedEntityData is falsey. Nothing to propagate.`)
    return
  }
  _propagateContractAdditionsChangesOrDeletions({
    sourceContract: deletedEntityData,
  })
}

async function _propagateContractAdditionsChangesOrDeletions({
  sourceContract,
}: {
  sourceContract: IContract
}) {
  async function __processOrderOverview({
    sourceContract,
  }: {
    sourceContract: IContract
  }) {
    if (!sourceContract.order) {
      console.debug(
        `${fn} No order associated with sourceContract.` +
          ` Nothing to propagate to order and children.`
      )
      return
    }

    const order = await getOrderDataById(sourceContract.order.id)
    if (!order) {
      throw new Error(`getOrderDataById returned undefined/null`)
    }
    const overviewData = await getOrderOverviewData(order.id)
    assertIsNotUndefined(overviewData)
    console.debug(`${fn} overviewData:`, overviewData)

    propagateContractData({
      callingFn: fn,
      overviewData,
    })
    console.debug(`${fn} overview after propagation:`, overviewData)

    // Can't just write out to localForage the one order that was changed.
    // Have to write the whole list.

    const orderList = await getOrderListData()
    if (!orderList) {
      throw new Error(`getOrderListData returned undefined/null`)
    }
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'order',
      listData: orderList,
      callingFn: fn,
    })

    const orderMSServiceList = await getOrderMSServiceListData()
    if (!orderList) {
      throw new Error(`getOrderListData returned undefined/null`)
    }
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'ordermsservices',
      listData: orderMSServiceList,
      callingFn: fn,
    })
    //
    //
    if (!sourceContract.company) {
      throw new Error(
        `${fn} No company associated with sourceContract.` +
          ` Nothing to propagate.`
      )
    }
  }
  //
  async function __processCompanyOverview({
    sourceContract,
  }: {
    sourceContract: IContract
  }) {
    const fn = '__processCompanyOverview |'
    console.debug(`${fn} running`)
    const company = await getCompanyDataById(sourceContract.company.id)
    if (!company) {
      throw new Error(`${fn} getCompanyDataById returned undefined/null`)
    }
    const companyOverviewData = await getCompanyOverviewData(company.id)
    assertIsNotUndefined(companyOverviewData)
    console.debug(`${fn} companyOverviewData:`, companyOverviewData)

    propagateContractData({
      callingFn: fn,
      overviewData: companyOverviewData,
    })
    console.debug(`${fn} overview after propagation:`, companyOverviewData)

    // Can't just write out to localForage the one company that was changed.
    // Have to write the whole list.

    const companyList = await getCompanyListData()
    if (!companyList) {
      throw new Error(`getCompanyListData returned undefined/null`)
    }
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'companies',
      listData: companyList,
      callingFn: fn,
    })

    const companyMSServiceList = await getCompanyMSServiceListData()
    if (!companyList) {
      throw new Error(`companyMSServiceList returned undefined/null`)
    }
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'companymsservices',
      listData: companyMSServiceList,
      callingFn: fn,
    })

    const allCompanyMSServicesInOverview = getAllMSServicesFromSiloOverview({
      overviewData: companyOverviewData,
    })
    const aCompanyMSServiceOnSourceContract =
      allCompanyMSServicesInOverview.find(
        (cmss: ICompanyMSService) => cmss.contract?.id === sourceContract.id
      )
    if (aCompanyMSServiceOnSourceContract) {
      propagateCompanyMSServiceChanges({
        targetItem: aCompanyMSServiceOnSourceContract,
      })
    }
    console.debug(`${fn} overview after propagation:`, companyOverviewData)

    const companyMasterServiceList = await getCompanyMasterServiceListData()
    if (!companyMasterServiceList) {
      throw new Error(`getCompanyMasterServiceListData returned undefined/null`)
    }
    console.debug(`${fn} companyMasterServiceList:`, companyMasterServiceList)
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'companymasterservices',
      listData: companyMasterServiceList,
      callingFn: fn,
    })
    const cmsList = await getCompanyMasterServiceListData()
    const dupeCmsList = cloneDeep(cmsList)
    console.debug(
      `${fn} companyMasterServiceList after storing, getting, and duping:`,
      dupeCmsList
    )
  }
  //
  // Execution starts here
  //
  const fn = `${mn} _propagateContractAdditionsChangesOrDeletions |`
  console.debug(`${fn} running`)
  if (!sourceContract) {
    console.debug(`${fn} sourceContract is falsey. Nothing to propagate.`)
    return
  }
  //
  // Don't need to propagate anything for an opportunityOverview
  // server-side at the moment. Handling orderOverview and
  // companyOverview below.
  //
  await __processOrderOverview({ sourceContract })
  await __processCompanyOverview({ sourceContract })
}

export async function getStartingContractListData(): Promise<IContract[]> {
  const cctList = await getCompanyContactListData()
  // const sctList = await getSupplierContactListData()
  // const tctList = await getTsdContactListData()
  // const oList = await getOpportunityListData()
  // const moList = await getOrderListData()
  // const tsdList = await getTsdListData()
  // const sList = await getSupplierListData()

  const plist = await getPersonListData()
  const scrList = await getSupplierContactRoleListData()

  return [
    {
      id: 'XXCON0001',
      hrid: 'CON0001',
      parentContract: undefined,
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      opportunity: undefined,
      order: undefined,
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: { id: 'XXS0002', label: 'Cogent' }, //  _getSDataById('XXS0002', await getSupplierListData()),
      descr: 'Cogent-Metropolis MSA',
      contractType: getContractTypeById(ContractTypeIds.MSA),
      contractNumber: 'MSA-29394',
      signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
      signedOnDate: '2024-01-01',
      startDate: '2024-02-01',
      endDate: null,
      term: 36,
      notes: '',
      status: getContractStatusById(ContractStatusIds.SIGNED),
      createdAt: '2024-01-01T19:00:00.000Z',
      modifiedAt: '2024-01-01T12:00:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: null,
      submittedToSupplierDate: null,
      submittedToTsdDate: null,
      acceptedBySupplierContact: undefined,
      acceptedOnDate: null,
      acceptedBySupplierDate: null,
      countersignedOnDate: null,
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      numOpportunityMSServices: 1,
      numOrderMSServices: 1,
      numCompanyMSServices: 1,
    },
    {
      id: 'XXCON0002',
      hrid: 'CON0002',
      parentContract: {
        id: 'XXCON0001',
        hrid: 'CON0001',
        parentContract: undefined,
        company: {
          id: 'XXC0001',
          label: 'Metropolis Bank',
        },
        opportunity: undefined,
        order: undefined,
        supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
        supplier: { id: 'XXS0002', label: 'Cogent' }, // _getSDataById('XXS0002', await getSupplierListData()),
        descr: 'Cogent-Metropolis MSA',
        contractType: getContractTypeById(ContractTypeIds.MSA),
        contractNumber: 'MSA-29394',
        signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
        signedOnDate: '2024-01-01',
        startDate: '2024-02-01',
        endDate: null,
        term: 36,
        notes: '',
        status: getContractStatusById(ContractStatusIds.SIGNED),
        createdAt: '2024-01-01T19:00:00.000Z',
        modifiedAt: '2024-01-01T12:00:00.000Z',
        version: '1',
        canBeDeleted: false,
        reviewedBy: {
          id: 'XXTM0001',
          label: 'Meryl Streep',
        },
        reviewedOnDate: null,
        submittedToSupplierDate: null,
        submittedToTsdDate: null,
        acceptedBySupplierContact: undefined,
        acceptedOnDate: null,
        acceptedBySupplierDate: null,
        countersignedOnDate: null,
        acceptedByTsdContact: undefined,
        acceptedByTsdDate: null,
        numOpportunityMSServices: 4,
        numOrderMSServices: 0,
        numCompanyMSServices: 0,
      }, // { id: 'XXCON0001', label: 'MSA-29394' },
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      opportunity: { id: 'XXO0003', label: 'DIA - Boston, Lake & Waltham' },
      // order: { id: 'XXORD0001', label: 'DIA - Target & Big Mac' },
      order: undefined,
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: { id: 'XXS0002', label: 'Cogent' }, // _getSDataById('XXS0002', await getSupplierListData()),
      descr: 'DIA - Boston & Lake',
      contractType: getContractTypeById(ContractTypeIds.SA),
      contractNumber: 'SA939200',
      signedByCompanyContact: undefined, // _getCCTDataById('XXCCT0001', cctList),
      signedOnDate: null,
      startDate: '2024-03-01',
      endDate: null,
      term: 36,
      notes: 'There may be an additional location added next week.',
      status: getContractStatusById(ContractStatusIds.DRAFT),
      createdAt: '2024-02-02T17:15:00.000Z',
      modifiedAt: '2024-02-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: null,
      submittedToSupplierDate: null,
      submittedToTsdDate: null,
      acceptedBySupplierContact: undefined,
      acceptedOnDate: null,
      acceptedBySupplierDate: null,
      countersignedOnDate: null,
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      numOpportunityMSServices: 4,
      numOrderMSServices: 0,
      numCompanyMSServices: 0,
    },
    {
      id: 'XXCON0003',
      hrid: 'CON0003',
      parentContract: {
        id: 'XXCON0001',
        hrid: 'CON0001',
        parentContract: undefined,
        company: {
          id: 'XXC0001',
          label: 'Metropolis Bank',
        },
        opportunity: undefined,
        order: undefined,
        supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
        supplier: { id: 'XXS0002', label: 'Cogent' }, // _getSDataById('XXS0002', await getSupplierListData()),
        descr: 'Cogent-Metropolis MSA',
        contractType: getContractTypeById(ContractTypeIds.MSA),
        contractNumber: 'MSA-29394',
        signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
        signedOnDate: '2024-01-01',
        startDate: '2024-02-01',
        endDate: null,
        term: 36,
        notes: '',
        status: getContractStatusById(ContractStatusIds.SIGNED),
        createdAt: '2024-01-01T19:00:00.000Z',
        modifiedAt: '2024-01-01T12:00:00.000Z',
        version: '1',
        canBeDeleted: false,
        reviewedBy: {
          id: 'XXTM0001',
          label: 'Meryl Streep',
        },
        reviewedOnDate: null,
        submittedToSupplierDate: null,
        submittedToTsdDate: null,
        acceptedBySupplierContact: undefined,
        acceptedOnDate: null,
        acceptedBySupplierDate: null,
        countersignedOnDate: null,
        acceptedByTsdContact: undefined,
        acceptedByTsdDate: null,
        numOpportunityMSServices: 1,
        numOrderMSServices: 1,
        numCompanyMSServices: 1,
      }, // { id: 'XXCON0001', label: 'MSA-29394' },
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      // opportunity: { id: 'XXO0003', label: 'DIA - Boston & Lake' },
      opportunity: undefined,
      order: { id: 'XXORD0001', label: 'DIA - Target & Big Mac' },
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: { id: 'XXS0002', label: 'Cogent' }, // _getSDataById('XXS0002', await getSupplierListData()),
      descr: 'DIA - Target & Big Mac',
      contractType: getContractTypeById(ContractTypeIds.SA),
      contractNumber: 'SA939403',
      signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
      signedOnDate: '2025-06-01',
      startDate: '2025-06-01',
      endDate: null,
      term: 36,
      notes: 'There may be an additional location added next week.',
      status: getContractStatusById(ContractStatusIds.ACCEPTED),
      createdAt: '2025-03-02T17:15:00.000Z',
      modifiedAt: '2025-03-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: '2025-05-10',
      submittedToSupplierDate: '2025-05-10',
      submittedToTsdDate: null,
      acceptedBySupplierContact: {
        id: 'XXSCT0002',
        hrid: 'SCT0002',
        person: _getPDataById('XXPE0019', plist),
        phone: '(520) 494-4049',
        cell: '(555) 999-8869',
        email: 'james@cogent.com',
        supplier: {
          id: 'XXS0002',
          label: 'Cogent',
        },
        role: _getSCRDataById('XXSCR003', scrList),
        // useFor: [getResourceUseById(ResourceUseIds.REGISTRATIONS)], // for testing
        status: getContactStatusById(ContactStatusIds.ACTIVE),
        version: '1',
        canBeDeleted: false,
      },
      acceptedOnDate: '2025-05-10',
      acceptedBySupplierDate: '2025-05-10',
      countersignedOnDate: '2025-05-14',
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      numOpportunityMSServices: 0,
      numOrderMSServices: 4,
      numCompanyMSServices: 0,
    },
    //
    //
    {
      id: 'XXCON0004',
      hrid: 'CON0004',
      parentContract: undefined,
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      opportunity: undefined,
      order: undefined,
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: { id: 'XXS0009', label: 'PacTel' }, //  _getSDataById('XXS0009', await getSupplierListData()),
      descr: 'PacTel-Metropolis MSA',
      contractType: getContractTypeById(ContractTypeIds.MSA),
      contractNumber: 'MSA-44444',
      signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
      signedOnDate: '2024-01-01',
      startDate: '2024-02-01',
      endDate: null,
      term: 36,
      notes: '',
      status: getContractStatusById(ContractStatusIds.SIGNED),
      createdAt: '2024-01-01T19:00:00.000Z',
      modifiedAt: '2024-01-01T12:00:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: null,
      submittedToSupplierDate: null,
      submittedToTsdDate: null,
      acceptedBySupplierContact: undefined,
      acceptedOnDate: null,
      acceptedBySupplierDate: null,
      countersignedOnDate: null,
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      numOpportunityMSServices: 0,
      numOrderMSServices: 0,
      numCompanyMSServices: 10,
    },
    //
    //
    {
      id: 'XXCON0005',
      hrid: 'CON0005',
      parentContract: {
        id: 'XXCON0004',
        hrid: 'CON0004',
        parentContract: undefined,
        company: {
          id: 'XXC0001',
          label: 'Metropolis Bank',
        },
        opportunity: undefined,
        order: undefined,
        supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
        supplier: { id: 'XXS0009', label: 'PacTel' }, //  _getSDataById('XXS0009', await getSupplierListData()),
        descr: 'PacTel-Metropolis MSA',
        contractType: getContractTypeById(ContractTypeIds.MSA),
        contractNumber: 'MSA-44444',
        signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
        signedOnDate: '2024-01-01',
        startDate: '2024-02-01',
        endDate: null,
        term: 36,
        notes: '',
        status: getContractStatusById(ContractStatusIds.SIGNED),
        createdAt: '2024-01-01T19:00:00.000Z',
        modifiedAt: '2024-01-01T12:00:00.000Z',
        version: '1',
        canBeDeleted: false,
        reviewedBy: {
          id: 'XXTM0001',
          label: 'Meryl Streep',
        },
        reviewedOnDate: null,
        submittedToSupplierDate: null,
        submittedToTsdDate: null,
        acceptedBySupplierContact: undefined,
        acceptedOnDate: null,
        acceptedBySupplierDate: null,
        countersignedOnDate: null,
        acceptedByTsdContact: undefined,
        acceptedByTsdDate: null,
        numOpportunityMSServices: 0,
        numOrderMSServices: 0,
        numCompanyMSServices: 10,
      },
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      // opportunity: { id: 'XXO0003', label: 'DIA - Boston & Lake' },
      opportunity: undefined,
      order: undefined,
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      descr: 'POTS',
      contractType: getContractTypeById(ContractTypeIds.SA),
      contractNumber: 'SA800002',
      signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
      signedOnDate: '2024-10-01',
      startDate: '2025-10-01',
      endDate: '2028-09-30',
      term: 36,
      notes: 'At least five per location',
      status: getContractStatusById(ContractStatusIds.ACCEPTED),
      createdAt: '2024-10-02T17:15:00.000Z',
      modifiedAt: '2024-10-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: '2024-10-10',
      submittedToSupplierDate: '2024-10-10',
      submittedToTsdDate: null,
      acceptedBySupplierContact: {
        id: 'XXSCT0002',
        hrid: 'SCT0002',
        person: _getPDataById('XXPE0019', plist),
        phone: '(520) 494-4049',
        cell: '(555) 999-8869',
        email: 'james@cogent.com',
        supplier: {
          id: 'XXS0002',
          label: 'Cogent',
        },
        role: _getSCRDataById('XXSCR003', scrList),
        // useFor: [getResourceUseById(ResourceUseIds.REGISTRATIONS)], // for testing
        status: getContactStatusById(ContactStatusIds.ACTIVE),
        version: '1',
        canBeDeleted: false,
      },
      acceptedOnDate: '2024-10-10',
      acceptedBySupplierDate: '2024-10-10',
      countersignedOnDate: '2024-10-12',
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      numOpportunityMSServices: 0,
      numOrderMSServices: 0,
      numCompanyMSServices: 10,
    },
    /**
     * XXCON0006 -- the fixture's ONLY "Deal" contract, and the reason it exists.
     *
     * A **Deal** is what sits on a SIGNED non-MSA contract: the thing the
     * Opportunity Overview's "Exclude Deals" filter hides, leaving only what is
     * still an open opportunity. Before this entry the mock could not express a
     * Deal at all -- XXCON0001/0004 are MSAs (exempt by definition), XXCON0003
     * and XXCON0005 belong to no opportunity, and XXCON0002 is DRAFT.
     *
     * ⚠ XXCON0002 IS DELIBERATELY LEFT DRAFT and must stay that way: it is
     * how a signature is exercised BY HAND, and it is what keeps the red
     * "contracts with Closed/Won services have not been signed" banner
     * reachable on the Opportunity Sales page
     * (`hasUnsignedContractsWithClosedWonServices` matches `status.id === DRAFT`
     * exactly). So the Deal is seeded as a SECOND, separately signed SA rather
     * than by promoting that one.
     *
     * 🔑 The pair is the point. XXO0003 now carries Closed/Won services on a
     * NON-terminal contract (XXCON0002, draft -> stays visible) AND on a
     * terminal one (this, signed -> hidden as a Deal). Only a fixture holding
     * BOTH distinguishes `dealMSService`'s AND from an OR, which is the error
     * the definitions were rewritten to avoid.
     *
     * The location is Waltham (XXL0009), a third site alongside Boston and
     * Lake -- exactly the "additional location added next week" that
     * XXCON0002's own `notes` field anticipates. Its services are a duplicate
     * of the Cogent DIA kit rather than a reassignment of any existing row, so
     * nothing already on XXCON0002 moves.
     */
    {
      id: 'XXCON0006',
      hrid: 'CON0006',
      parentContract: {
        id: 'XXCON0001',
        hrid: 'CON0001',
        parentContract: undefined,
        company: {
          id: 'XXC0001',
          label: 'Metropolis Bank',
        },
        opportunity: undefined,
        order: undefined,
        supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
        supplier: { id: 'XXS0002', label: 'Cogent' }, // _getSDataById('XXS0002', await getSupplierListData()),
        descr: 'Cogent-Metropolis MSA',
        contractType: getContractTypeById(ContractTypeIds.MSA),
        contractNumber: 'MSA-29394',
        signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
        signedOnDate: '2024-01-01',
        startDate: '2024-02-01',
        endDate: null,
        term: 36,
        notes: '',
        status: getContractStatusById(ContractStatusIds.SIGNED),
        createdAt: '2024-01-01T19:00:00.000Z',
        modifiedAt: '2024-01-01T12:00:00.000Z',
        version: '1',
        canBeDeleted: false,
        reviewedBy: {
          id: 'XXTM0001',
          label: 'Meryl Streep',
        },
        reviewedOnDate: null,
        submittedToSupplierDate: null,
        submittedToTsdDate: null,
        acceptedBySupplierContact: undefined,
        acceptedOnDate: null,
        acceptedBySupplierDate: null,
        countersignedOnDate: null,
        acceptedByTsdContact: undefined,
        acceptedByTsdDate: null,
        numOpportunityMSServices: 4,
        numOrderMSServices: 0,
        numCompanyMSServices: 0,
      }, // { id: 'XXCON0001', label: 'MSA-29394' },
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      opportunity: { id: 'XXO0003', label: 'DIA - Boston, Lake & Waltham' },
      // ⚠ A SIGNED non-MSA contract MUST carry an order, and this field is what
      // says the signature was acted on. Signing runs the `contractUpdated`
      // handler (SiloOverview.ts), which builds an order overview from the
      // opportunity and POSTs it to /api/orders/overview; the last thing that
      // routine does is stamp the new order back onto the contract
      // (`_setOrderInSignedUnclonedNonMSAContracts`, cynqueModules/dataObjects/
      // Opportunity.ts). A signed contract with `order: undefined` is therefore
      // not a finished state but a TRANSIENT one -- it is precisely what
      // `_getSignedUnclonedNonMSAContractsInOpportunityOverview` selects as
      // still needing to be cloned, so seeding it that way would leave the
      // fixture mid-routine and hand the next signature a duplicate order.
      //
      // The label is the ORDER's `descr`, which is what that function copies.
      order: { id: 'XXORD0002', label: 'DIA - Boston, Lake & Waltham' },
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: { id: 'XXS0002', label: 'Cogent' },
      descr: 'DIA - Waltham',
      contractType: getContractTypeById(ContractTypeIds.SA),
      contractNumber: 'SA939201',
      signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
      signedOnDate: '2024-03-15',
      startDate: '2024-04-01',
      endDate: null,
      term: 36,
      notes: 'Waltham turn-up. Signed; services are Closed/Won.',
      status: getContractStatusById(ContractStatusIds.SIGNED),
      createdAt: '2024-03-01T15:00:00.000Z',
      modifiedAt: '2024-03-15T15:00:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: '2024-03-10',
      submittedToSupplierDate: null,
      submittedToTsdDate: null,
      acceptedBySupplierContact: undefined,
      acceptedOnDate: null,
      acceptedBySupplierDate: null,
      countersignedOnDate: null,
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      // The four kit ITEMS. `isCountableMSService` requires `isItemProduct`, so
      // the kit ROOT (XXOMSS0131) does not count -- same as XXCON0002, whose
      // five Closed/Won rows likewise count as four items.
      // Recomputed by `_propagateFromOMSServicesToContracts` on every load; the
      // seed is kept honest so a pre-cascade read is not misleading.
      numOpportunityMSServices: 4,
      numOrderMSServices: 0,
      numCompanyMSServices: 0,
    },
    /**
     * XXCON0007 / XXCON0008 -- RingCentral's MSA and its SA on XXO0003.
     *
     * They exist to satisfy documentation/rules/contract: a contract is
     * SUPPLIER-SPECIFIC and can only contain msServices for that supplier.
     * XXOMSS0130 ('TAM - Bronze') is a RingCentral service and used to sit on
     * XXCON0002, which is Cogent's -- the one violation of that rule anywhere in
     * the fixture, and the reason the rule was written down. Moving it needed a
     * RingCentral contract to move it TO, and an SA hangs off its supplier's own
     * MSA (as XXCON0005 does off XXCON0004), so both were added together.
     *
     * ⚠ XXCON0008 IS DRAFT, DELIBERATELY. XXOMSS0130 is CLOSED WON, and a
     * Closed/Won service on an unsigned contract is exactly the still-open state
     * XXCON0002 already models -- so the row keeps the shape it was created for
     * (see its own comment in opportunityMSServices/mockData.ts: level ITEM, no
     * parentMSServiceId, CLOSED_WON, ON A CONTRACT) without becoming a Deal.
     * Signing it would additionally oblige an order to exist, per the
     * signed-implies-cloned rule that XXCON0006 documents below.
     *
     * The MSA is SIGNED like the other two, which costs nothing: MSAs are
     * filtered out of `_getSignedUnclonedNonMSAContractsInOpportunityOverview`
     * entirely, so a signed MSA never owes an order.
     */
    {
      id: 'XXCON0007',
      hrid: 'CON0007',
      parentContract: undefined,
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      opportunity: undefined,
      order: undefined,
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: { id: 'XXS0005', label: 'RingCentral' },
      descr: 'RingCentral-Metropolis MSA',
      contractType: getContractTypeById(ContractTypeIds.MSA),
      contractNumber: 'MSA-77120',
      signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
      signedOnDate: '2024-01-15',
      startDate: '2024-02-01',
      endDate: null,
      term: 36,
      notes: '',
      status: getContractStatusById(ContractStatusIds.SIGNED),
      createdAt: '2024-01-15T16:00:00.000Z',
      modifiedAt: '2024-01-15T16:00:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: null,
      submittedToSupplierDate: null,
      submittedToTsdDate: null,
      acceptedBySupplierContact: undefined,
      acceptedOnDate: null,
      acceptedBySupplierDate: null,
      countersignedOnDate: null,
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      numOpportunityMSServices: 0,
      numOrderMSServices: 0,
      numCompanyMSServices: 0,
    },
    {
      id: 'XXCON0008',
      hrid: 'CON0008',
      parentContract: {
        id: 'XXCON0007',
        hrid: 'CON0007',
        parentContract: undefined,
        company: {
          id: 'XXC0001',
          label: 'Metropolis Bank',
        },
        opportunity: undefined,
        order: undefined,
        supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
        supplier: { id: 'XXS0005', label: 'RingCentral' },
        descr: 'RingCentral-Metropolis MSA',
        contractType: getContractTypeById(ContractTypeIds.MSA),
        contractNumber: 'MSA-77120',
        signedByCompanyContact: _getCCTDataById('XXCCT0001', cctList),
        signedOnDate: '2024-01-15',
        startDate: '2024-02-01',
        endDate: null,
        term: 36,
        notes: '',
        status: getContractStatusById(ContractStatusIds.SIGNED),
        createdAt: '2024-01-15T16:00:00.000Z',
        modifiedAt: '2024-01-15T16:00:00.000Z',
        version: '1',
        canBeDeleted: false,
        reviewedBy: {
          id: 'XXTM0001',
          label: 'Meryl Streep',
        },
        reviewedOnDate: null,
        submittedToSupplierDate: null,
        submittedToTsdDate: null,
        acceptedBySupplierContact: undefined,
        acceptedOnDate: null,
        acceptedBySupplierDate: null,
        countersignedOnDate: null,
        acceptedByTsdContact: undefined,
        acceptedByTsdDate: null,
        numOpportunityMSServices: 0,
        numOrderMSServices: 0,
        numCompanyMSServices: 0,
      },
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      opportunity: { id: 'XXO0003', label: 'DIA - Boston, Lake & Waltham' },
      order: undefined,
      supplierType: getSupplierTypeById(SupplierTypeIds.SUPPLIER),
      supplier: { id: 'XXS0005', label: 'RingCentral' },
      descr: 'TAM - Boston',
      contractType: getContractTypeById(ContractTypeIds.SA),
      contractNumber: 'SA771201',
      signedByCompanyContact: undefined,
      signedOnDate: null,
      startDate: '2024-04-01',
      endDate: null,
      term: 36,
      notes: '',
      status: getContractStatusById(ContractStatusIds.DRAFT),
      createdAt: '2024-03-01T16:00:00.000Z',
      modifiedAt: '2024-03-01T16:00:00.000Z',
      version: '1',
      canBeDeleted: false,
      reviewedBy: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      reviewedOnDate: null,
      submittedToSupplierDate: null,
      submittedToTsdDate: null,
      acceptedBySupplierContact: undefined,
      acceptedOnDate: null,
      acceptedBySupplierDate: null,
      countersignedOnDate: null,
      acceptedByTsdContact: undefined,
      acceptedByTsdDate: null,
      numOpportunityMSServices: 1,
      numOrderMSServices: 0,
      numCompanyMSServices: 0,
    },
  ]
}
